'use strict';

import { assignDeep, isDesktop, isMobile, threeJsVariables } from '../../settings';
import { OrbitDayAndNight } from '../liveAnimations/DayNight/OrbitDayAndNight';
import { Color, DirectionalLight, LinearSRGBColorSpace } from 'three';
import { AmbientLight } from 'three';
import { Group } from 'three';
import { bundleVersionEquals } from './autoProjectScript';

const DIRECTIONAL_LIGHT_DEFAULT_SETTINGS = {
    color: "#ffffff",
    intensity: 1,
    castShadow: true,
    position: {
        x: 100,
        y: 100,
        z: 0
    },
    shadow: {
        mapSize: {
            x: 1024,
            y: 1024
        },
        normalBias: 0,
        camera: {
            near: 0.5,
            far: 2000,
            size: 150
        }
    }
};
const AMBIENT_LIGHT_DEFAULT_SETTINGS = {
    color: "#ffffff",
    intensity: 0
};

const radToDeg = r=>r*(180/Math.PI)
const degToRad = d=>d*(Math.PI/180)

export function addLights () {
    const directionalLightSettings = assignDeep(DIRECTIONAL_LIGHT_DEFAULT_SETTINGS,config.threejs.lights.directionalLight);
    const ambientLightSettings = assignDeep(AMBIENT_LIGHT_DEFAULT_SETTINGS,config.threejs.lights.ambientLight);
    const directionalLight = createDirectionalLight(directionalLightSettings);
    directionalLight.intensity *= Math.PI;
    const ambientLight = createAmbientLight(ambientLightSettings);
    ambientLight.intensity *= Math.PI;

    const lightGroup = new Group();
    lightGroup.name = 'light_group';
    lightGroup.add(ambientLight,directionalLight);
    
    if (config.threejs.lights.secondDirectionalLight && config.threejs.lights.secondDirectionalLight.coefficient !== 0) {
        const secondDirectionalLight = createSecondDirectionalLight(directionalLight);
        lightGroup.add(secondDirectionalLight);
    }

    threeJsVariables.scene.add(lightGroup);
}

function createDirectionalLight (settings = DIRECTIONAL_LIGHT_DEFAULT_SETTINGS) {
    const light = new DirectionalLight(new Color().setStyle(settings.color,LinearSRGBColorSpace),settings.intensity);
    light.name = "Dir. Light";
    light.castShadow = settings.castShadow;
    light.position.set(settings.position.x,settings.position.y,settings.position.z);
    light.shadow.normalBias = threeJsVariables.quality.shadowNormalBias(settings.shadow.normalBias);
    light.shadow.mapSize.width = settings.shadow.mapSize.x;
    light.shadow.mapSize.height = settings.shadow.mapSize.y;
    light.shadow.camera.near = settings.shadow.camera.near;
    light.shadow.camera.far = settings.shadow.camera.far;
    light.shadow.camera.right = settings.shadow.camera.size;
    light.shadow.camera.left = - settings.shadow.camera.size;
    light.shadow.camera.top	= settings.shadow.camera.size;
    light.shadow.camera.bottom = - settings.shadow.camera.size;
    light.layers.enable(1);

    if (OrbitDayAndNight.isActiveAndSelected) {
        light.castShadow = config.features.dayNight.settings.shadows;
        if (isDesktop) {
            light.shadow.mapSize.width = 2048;
            light.shadow.mapSize.height = 2048;
            light.shadow.camera.right = 2000;
            light.shadow.camera.left = - 2000;
            light.shadow.camera.top	= 2000;
            light.shadow.camera.bottom = - 2000;
            light.shadow.camera.far = 4000;
        }
    }
    
    light.shadow.mapSize.width = threeJsVariables.quality.shadowMapSize(settings.shadow.mapSize.x);
    light.shadow.mapSize.height = threeJsVariables.quality.shadowMapSize(settings.shadow.mapSize.y);
    light.shadow.camera.right = threeJsVariables.quality.shadowCameraSize(settings.shadow.camera.size);
    light.shadow.camera.left = - threeJsVariables.quality.shadowCameraSize(settings.shadow.camera.size);
    light.shadow.camera.top	= threeJsVariables.quality.shadowCameraSize(settings.shadow.camera.size);
    light.shadow.camera.bottom = - threeJsVariables.quality.shadowCameraSize(settings.shadow.camera.size);
    
    light.shadow.camera.updateProjectionMatrix();

    return light;
}

function createSecondDirectionalLight (source) {
    const light = new DirectionalLight().copy(source);
    light.name = 'Second directional light';
    light.castShadow = false;
    const settings = config.threejs.lights.secondDirectionalLight || {coefficient: 0};

    light.updateParams = () => {
        light.position.copy(source.position).negate();
        light.color.copy(source.color);
        light.intensity = source.intensity * settings.coefficient;
    };

    light.updateParams();

    return light;
}

function createAmbientLight (settings = AMBIENT_LIGHT_DEFAULT_SETTINGS) {
    const light = new AmbientLight(new Color().setStyle(settings.color,LinearSRGBColorSpace),settings.intensity);
    light.name = "ambientLight";
    light.layers.enable(1);

    return light;
}

export function updateShadowMap () {
    if (threeJsVariables.renderer.shadowMap.autoUpdate)
        return;
    
    threeJsVariables.renderer.shadowMap.needsUpdate = true;
    // if (OrbitDayAndNight.isActiveAndSelected) {
    //     threeJsVariables.renderer.shadowMap.needsUpdate = true;
    // } else {
    //     //if (isMobile) return;
    //
    //     threeJsVariables.renderer.shadowMap.needsUpdate = true;
    // }
}

const setSunAngles = (angleh,anglev)=>{

    if (angleh>(2*Math.PI)){
        angleh = angleh - 2*Math.PI
    }
    if (angleh<0){
        angleh = 2*Math.PI + angleh
    }
    let x1 = Math.cos(angleh)
    let y1 = 0
    let z1 = Math.sin(angleh)

    let x2 = x1*Math.cos(anglev)
    let y2 = Math.sin(anglev)
    let z2 = z1*Math.cos(anglev)

    // TODO distance
    const distance = 100
	let d = Math.trunc(distance)

    let x = 0 + x2*d
    let y = 0 + y2*d
    let z = 0 + z2*d

	x = Math.trunc(x)
	y = Math.trunc(y)
	z = Math.trunc(z)

    let v = threeJsVariables.scene.getObjectByName('Dir. Light');
	v.position.set(x,y,z)
    v.target.position.set(0,0,0)
	v.target.updateMatrixWorld()
}

export const bundleSetSun = (bundleConfig)=>{
    let angleh = degToRad(parseInt(bundleConfig.sun.angleh) || 0)
	  let anglev = degToRad(parseInt(bundleConfig.sun.anglev) || 0)

    setSunAngles(angleh,anglev)

    let v = threeJsVariables.scene.getObjectByName('Dir. Light');
    const ambientLight = threeJsVariables.scene.getObjectByName('ambientLight');
    const secondDirectionalLight = threeJsVariables.scene.getObjectByName('Second directional light');
    v.color.setStyle('#'+bundleConfig.sun.dircolor,LinearSRGBColorSpace);
    v.intensity = parseFloat(bundleConfig.sun.dirintensity) * Math.PI;
    ambientLight.color.setStyle('#'+bundleConfig.sun.ambcolor,LinearSRGBColorSpace);
    ambientLight.intensity = parseFloat(bundleConfig.sun.ambintensity) * Math.PI;
    secondDirectionalLight?.updateParams();
    updateShadowMap();
}

export function prepareLight (bundleConfig) {
   const settings = bundleConfig.lights;
   
   if (bundleVersionEquals(1)) {
       settings.directionalLight.intensity *= Math.PI;
       settings.ambientLight.intensity *= Math.PI;
   }
   
   const directionalLight = threeJsVariables.scene.getObjectByName('Dir. Light');
   const ambientLight = threeJsVariables.scene.getObjectByName('ambientLight');
   const secondDirectionalLight = threeJsVariables.scene.getObjectByName('Second directional light');

   const tempDirectional = createDirectionalLight(settings.directionalLight);
   const tempAmbient = createAmbientLight(settings.ambientLight);
   directionalLight.copy(tempDirectional);
   directionalLight.shadow.normalBias = tempDirectional.shadow.normalBias;
   ambientLight.copy(tempAmbient);
   tempDirectional.dispose();
   tempAmbient.dispose();
   secondDirectionalLight?.updateParams();
   updateShadowMap();
}
